import { useCallback, useEffect, useMemo, useRef } from 'react';
import { IChangeEvent } from '@rjsf/core';
import { useDependencyManager } from './DependencyManager';
import { useApi } from '@backstage/core-plugin-api';
import { errorApiRef } from '@backstage/core-plugin-api';
import { DEPENDENCY_MANAGER_ID_SEPARATOR, Step } from './utils';

interface UseDependencyManagedFormProps {
  formData: Record<string, any>;
  onChange: (e: IChangeEvent) => void;
  currentStep: Step | null;
}

interface UseDependencyManagedFormResult {
  dependencyManager: any;
  memoizedSchema: Record<string, any>;
  handleFormChange: (e: IChangeEvent) => void;
  handleResetForm: () => void;
}

/**
 * Custom hook to manage form data with dependency manager integration.
 * Centralizes logic for both MultistepJsonFormComponent and MultiStepJsonEditFormComponent.
 *
 * Note: This hook passes formData directly to useDependencyManager. The dependency manager
 * maintains its own formDataRef that is updated immediately after each batch (before React re-renders),
 * which prevents race conditions when multiple batches are applied in quick succession.
 */
export const useDependencyManagedForm = ({
  formData,
  onChange,
  currentStep,
}: UseDependencyManagedFormProps): UseDependencyManagedFormResult => {
  const errorApi = useApi(errorApiRef);

  // Ref for stable callback reference (avoid recreating batch manager on every render)
  const onChangeRef = useRef(onChange);

  // Keep ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Memoize config to avoid middleware recreation
  const memoizedConfig = useMemo(
    () => ({
      idSeparator: DEPENDENCY_MANAGER_ID_SEPARATOR,
    }),
    [],
  );

  // Memoize current step schema to avoid middleware recreation on every formData change
  const memoizedCurrentStepSchema = useMemo(
    () => currentStep?.mergedSchema,
    [currentStep?.mergedSchema],
  );

  // Memoize schema to avoid middleware recreation on every formData change
  const emptySchema = useMemo(() => ({}), []);
  const memoizedSchema = useMemo(
    () => memoizedCurrentStepSchema ?? emptySchema,
    [memoizedCurrentStepSchema, emptySchema],
  );

  // Create a CONSTANT middleware key that never changes
  // This ensures the middleware persists across all steps, allowing cross-step dependencies
  // The middleware schema will be updated via onFormDataChange as steps change, but the
  // instance itself persists with all registered handlers and dependencies intact
  const middlewareKey = useMemo(() => {
    // Use a constant string so middleware is never recreated when switching steps
    return 'multi-step-form-middleware';
  }, []);

  // Memoize onFormDataChange to prevent batch manager recreation on every render
  const handleFormDataChange = useCallback(
    (newFormData: Record<string, any>) => {
      // Propagate changes to parent component
      onChangeRef.current({ formData: newFormData } as IChangeEvent);
    },
    [],
  );

  // Integrate dependency manager for the active step
  const dependencyManager = useDependencyManager({
    schema: memoizedSchema,
    formData,
    onFormDataChange: handleFormDataChange,
    config: memoizedConfig,
    middlewareKey,
  });

  // Show validation errors from dependency manager
  useEffect(() => {
    if (dependencyManager.validationErrors.length > 0) {
      errorApi.post({
        name: 'SchemaValidationError',
        message: `Template schema contains ${dependencyManager.validationErrors.length} validation error(s).`,
      });
    }
  }, [dependencyManager.validationErrors, errorApi]);

  // Form's onChange handler - notifies dependency manager of changes
  const handleFormChange = useCallback(
    (e: IChangeEvent) => {
      // Propagate changes to parent component
      onChange(e);

      // Notify dependency manager of the change so it can trigger dependent field handlers
      // Only notify for TOP-LEVEL fields, not array items
      // Array items notify through their own notifyUpdate calls (EntityPicker, EntitySelectionPicker, etc)
      if (dependencyManager?.notifyUpdate) {
        const oldData = formData || {};
        const newData = e.formData || {};

        // Only process top-level fields (not array items like dataAssets, which are handled by child components)
        Object.keys(newData).forEach(key => {
          // Skip array fields - they handle their own notifications through child components
          if (Array.isArray(newData[key])) {
            return;
          }

          if (newData[key] !== oldData[key]) {
            dependencyManager.notifyUpdate({
              fieldPath: key,
              value: newData[key],
              source: 'user',
              intent: 'manual',
            });
          }
        });
      }
    },
    [onChange, dependencyManager, formData],
  );

  const handleResetForm = useCallback(() => {
    onChange({ formData: {} } as IChangeEvent);
  }, [onChange]);

  return {
    dependencyManager,
    memoizedSchema,
    handleFormChange,
    handleResetForm,
  };
};
